/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React from 'react';
import PropTypes from 'prop-types';
import NavItem from './NavItem.js';
import NavMenu from './NavMenu.js';
import { findActiveItem as _findActiveItem, isActiveItem } from '../utils/findActiveItem.js';
import NavFlyout from './NavFlyout.js';
import NavDivider from './NavDivider.js';

var _excluded = ["items", "flyoutItems", "onClick", "onKeyDown", "href", "to", "label"];
var NavItems = function NavItems(_ref) {
  var items = _ref.items,
    _ref$showMenuItems = _ref.showMenuItems,
    showMenuItems = _ref$showMenuItems === void 0 ? true : _ref$showMenuItems,
    activeHref = _ref.activeHref,
    _ref$linkComponent = _ref.linkComponent,
    LinkComponent = _ref$linkComponent === void 0 ? 'a' : _ref$linkComponent,
    onSelect = _ref.onSelect;
  return /*#__PURE__*/React.createElement("ul", {
    className: "cpc--side-nav__items"
  }, items.map(function (item, index) {
    var subItems = item.items,
      flyoutItems = item.flyoutItems,
      _onClick = item.onClick,
      _onKeyDown = item.onKeyDown,
      href = item.href,
      to = item.to,
      label = item.label,
      itemProps = _objectWithoutProperties(item, _excluded);
    if (itemProps.isDivider) {
      // eslint-disable-next-line react/no-array-index-key
      return /*#__PURE__*/React.createElement(NavDivider, {
        key: index
      });
    }
    if (flyoutItems) {
      return /*#__PURE__*/React.createElement(NavFlyout, _extends({
        key: "".concat(label, "-").concat(href),
        active: !!_findActiveItem(flyoutItems, activeHref),
        label: label,
        activeHref: activeHref,
        items: flyoutItems,
        onItemSelect: onSelect,
        linkComponent: LinkComponent
        // eslint-disable-next-line react/jsx-props-no-spreading
      }, itemProps));
    }
    if (subItems && showMenuItems) {
      return /*#__PURE__*/React.createElement(NavMenu, _extends({
        key: "".concat(label, "-").concat(href),
        active: !!_findActiveItem(subItems, activeHref),
        onClick: _onClick,
        label: label,
        activeHref: activeHref,
        items: subItems,
        linkComponent: LinkComponent,
        onItemSelect: onSelect
        // eslint-disable-next-line react/jsx-props-no-spreading
      }, itemProps));
    }
    return /*#__PURE__*/React.createElement(NavItem, _extends({
      key: href ? "".concat(label, "-").concat(href) : "".concat(label, "-").concat(to),
      active: isActiveItem(item, activeHref),
      linkComponent: href ? 'a' : LinkComponent,
      label: label,
      href: href,
      to: to,
      onClick: function onClick(evt) {
        return onSelect(evt, to || href, _onClick);
      },
      onKeyDown: function onKeyDown(evt) {
        return onSelect(evt, to || href, _onKeyDown);
      }
      // eslint-disable-next-line react/jsx-props-no-spreading
    }, itemProps));
  }));
};
NavItems.propTypes = {
  /**
   * The href for the current page the user is on.
   */
  activeHref: PropTypes.string.isRequired,
  /**
   * Whether or not the sub menu of items should be shown.
   */
  showMenuItems: PropTypes.bool,
  /**
   * A list of items to render in the nav.
   */
  items: PropTypes.arrayOf(PropTypes.shape({
    to: PropTypes.string,
    href: PropTypes.string,
    icon: PropTypes.shape({}),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    open: PropTypes.bool,
    items: PropTypes.arrayOf(PropTypes.shape({
      to: PropTypes.string,
      href: PropTypes.string,
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      flyoutItems: PropTypes.arrayOf(PropTypes.shape({
        to: PropTypes.string,
        href: PropTypes.string,
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
      }))
    }))
  })).isRequired,
  /**
   * The component to use for navigation links.
   */
  linkComponent: PropTypes.elementType,
  /**
   * An event to fire off upon selecting the
   */
  onSelect: PropTypes.func.isRequired
};
var NavItems$1 = NavItems;

export { NavItems$1 as default };
